/**
 * Event card pool and effect application.
 *
 * v2 ships 15 production cards covering the full educational message-space:
 *   1.  evt-server-crash         — high-risk territories without env-readiness lose cubes
 *   2.  evt-compliance-audit     — players without test policy lose a random territory
 *   3.  evt-flaky-test           — active player loses a random territory
 *   4.  evt-release-deadline     — sets releaseDeadlineTriggered (game-ender)
 *   5.  evt-production-incident  — random non-env-readiness territory loses 2 cubes
 *   6.  evt-audit-failure        — players without test plan lose 2 reserveCubes
 *   7.  evt-knowledge-loss       — random player loses one Tier-2/3 dashboard flag
 *   8.  evt-test-tool-adoption   — players with test plan + test model gain 1 reserveCube
 *   9.  evt-successful-audit     — players with full Tier-1 chain gain 1 reserveCube per fully-controlled module
 *   10. evt-data-breach          — random territory of a player without test plan goes neutral
 *   11. evt-defect-triage-backlog — every territory with >5 cubes loses 1 cube (forces dispersion)
 *   12. evt-environment-outage   — every territory without env-readiness loses 1 cube
 *   13. evt-acceptance-test-boom — players with full Tier-3 chain gain controlled-territories/4 reserveCubes
 *   14. evt-regression-win       — every non-eliminated player gains 1 reserveCube
 *   15. evt-test-charter-shortfall — players without test model lose env-readiness on a random territory
 *
 * Every effect is a pure function `(state, prng) → state`. Determinism is
 * enforced by deriving `prng` from `${seed}:${actionCount}` upstream.
 *
 * Tier-3 chain = hasTestPolicy + hasTestPlan + hasEnvReadiness + hasTestModel
 *                + at least 1 testCase + at least 1 testProcedure (per 29119-3 §8.3, §8.4).
 * Tier-1 chain = hasTestPolicy.
 * Tier-2 chain = hasTestPolicy + hasTestPlan.
 */

import type {
  EventCardId,
  GameState,
  PlayerId,
  TerritoryRuntimeState,
} from '@qd/protocol';
import type { Prng } from './prng.js';

export interface EventCardDef {
  readonly id: EventCardId;
  readonly displayName: string;
  readonly description: string;
  readonly isoClauseRef: string;
  readonly kind: 'bug' | 'audit' | 'system' | 'bonus';
  readonly effect: (state: GameState, prng: Prng) => GameState;
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function isHighRisk(_state: GameState, territoryId: string): boolean {
  const prefix = territoryId.split('-', 1)[0];
  return prefix === 'security' || prefix === 'compliance' || prefix === 'devops';
}

function ownedTerritories(state: GameState, playerId: PlayerId): TerritoryRuntimeState[] {
  return Object.values(state.territories).filter((t) => t.controllingPlayerId === playerId);
}

function decrementCubes(t: TerritoryRuntimeState, by: number): TerritoryRuntimeState {
  const next = Math.max(1, t.cubeCount - by);
  return { ...t, cubeCount: next };
}

function neutralise(t: TerritoryRuntimeState): TerritoryRuntimeState {
  return { ...t, controllingPlayerId: null, cubeCount: 0, hasEnvReadiness: false };
}

function patchPlayer(
  state: GameState,
  playerId: PlayerId,
  patch: Partial<GameState['players'][number]>,
): GameState {
  return {
    ...state,
    players: state.players.map((p) => (p.id === playerId ? { ...p, ...patch } : p)),
  };
}

function patchPlayerReserve(state: GameState, playerId: PlayerId, delta: number): GameState {
  return {
    ...state,
    players: state.players.map((p) =>
      p.id === playerId ? { ...p, reserveCubes: Math.max(0, p.reserveCubes + delta) } : p,
    ),
  };
}

function modulesFullyControlledBy(state: GameState, playerId: PlayerId): number {
  return Object.values(state.modules).filter((m) => m.fullyControlledByPlayerId === playerId).length;
}

function hasTier3Chain(p: GameState['players'][number]): boolean {
  const d = p.dashboard;
  return (
    d.hasTestPolicy &&
    d.hasTestPlan &&
    d.hasEnvReadiness &&
    d.hasTestModel &&
    d.testCases > 0 &&
    d.testProcedures > 0
  );
}

// -----------------------------------------------------------------------------
// Cards 1-4 (existing)
// -----------------------------------------------------------------------------

const serverCrash: EventCardDef = {
  id: 'evt-server-crash',
  displayName: 'Server Crash',
  description:
    'Every high-risk territory without an Env-Readiness flag loses 1 cube (minimum 1 cube remains).',
  isoClauseRef: 'ISO/IEC/IEEE 29119-3:2021 §8.6 / §8.8 (Test environment readiness)',
  kind: 'bug',
  effect: (state) => {
    const next: Record<string, TerritoryRuntimeState> = {};
    for (const [tid, t] of Object.entries(state.territories)) {
      if (
        t.controllingPlayerId &&
        t.cubeCount > 1 &&
        !t.hasEnvReadiness &&
        isHighRisk(state, tid)
      ) {
        next[tid] = { ...t, cubeCount: t.cubeCount - 1 };
      } else {
        next[tid] = t;
      }
    }
    return { ...state, territories: next };
  },
};

const complianceAudit: EventCardDef = {
  id: 'evt-compliance-audit',
  displayName: 'Compliance Audit',
  description: 'Each player without a Test Policy loses one random territory to neutral.',
  isoClauseRef: 'ISO/IEC/IEEE 29119-3:2021 §6.2 (Test policy)',
  kind: 'audit',
  effect: (state, prng) => {
    let territories = { ...state.territories } as Record<string, TerritoryRuntimeState>;
    for (const player of state.players) {
      if (player.dashboard.hasTestPolicy) continue;
      const owned = Object.values(territories).filter(
        (t) => t.controllingPlayerId === player.id,
      );
      if (owned.length === 0) continue;
      const victim = prng.pick(owned);
      territories = { ...territories, [victim.territoryId]: neutralise(victim) };
    }
    return { ...state, territories };
  },
};

const flakyTest: EventCardDef = {
  id: 'evt-flaky-test',
  displayName: 'Flaky Test',
  description: 'The active player loses one random territory to neutral.',
  isoClauseRef: 'ISO/IEC/IEEE 29119-3:2021 §3.15 (Test incident)',
  kind: 'bug',
  effect: (state, prng) => {
    const activeId = state.playerOrder[state.currentPlayerIdx];
    if (!activeId) return state;
    const owned = ownedTerritories(state, activeId);
    if (owned.length === 0) return state;
    const victim = prng.pick(owned);
    return {
      ...state,
      territories: { ...state.territories, [victim.territoryId]: neutralise(victim) },
    };
  },
};

const releaseDeadline: EventCardDef = {
  id: 'evt-release-deadline',
  displayName: 'Release Deadline',
  description:
    'The team has shipped. The game ends at the close of the current round; whoever controls the most territory wins.',
  isoClauseRef: 'ISO/IEC/IEEE 29119-3:2021 §7.4 (Test completion report)',
  kind: 'system',
  effect: (state) => ({ ...state, releaseDeadlineTriggered: true }),
};

// -----------------------------------------------------------------------------
// Card 5: Production Incident
// -----------------------------------------------------------------------------

const productionIncident: EventCardDef = {
  id: 'evt-production-incident',
  displayName: 'Production Incident',
  description:
    'A high-severity defect ships. One random territory without Env-Readiness loses 2 cubes (minimum 1).',
  isoClauseRef: 'ISO/IEC/IEEE 29119-3:2021 §8.11 (Test incident report) / §8.11.6 severity',
  kind: 'bug',
  effect: (state, prng) => {
    const candidates = Object.values(state.territories).filter(
      (t) => t.controllingPlayerId !== null && !t.hasEnvReadiness && t.cubeCount > 1,
    );
    if (candidates.length === 0) return state;
    const victim = prng.pick(candidates);
    return {
      ...state,
      territories: {
        ...state.territories,
        [victim.territoryId]: decrementCubes(victim, 2),
      },
    };
  },
};

// -----------------------------------------------------------------------------
// Card 6: Audit Failure
// -----------------------------------------------------------------------------

const auditFailure: EventCardDef = {
  id: 'evt-audit-failure',
  displayName: 'Audit Failure',
  description:
    'Tier-2 documentation incomplete. Each player without a Test Plan loses 2 reserveCubes (minimum 0).',
  isoClauseRef: 'ISO/IEC/IEEE 29119-3:2021 §7.2 (Test plan)',
  kind: 'audit',
  effect: (state) => {
    let next = state;
    for (const player of state.players) {
      if (player.dashboard.hasTestPlan) continue;
      next = patchPlayerReserve(next, player.id, -2);
    }
    return next;
  },
};

// -----------------------------------------------------------------------------
// Card 7: Knowledge Loss
// -----------------------------------------------------------------------------

const knowledgeLoss: EventCardDef = {
  id: 'evt-knowledge-loss',
  displayName: 'Knowledge Loss',
  description:
    'A key tester leaves. A random player loses one of their highest-tier dashboard documentation flags.',
  isoClauseRef: 'ISO/IEC/IEEE 29119-3:2021 §6.2.5 (Tester training) / §6.2.10 (Test asset archiving and reuse)',
  kind: 'bug',
  effect: (state, prng) => {
    const eligible = state.players.filter((p) => {
      const d = p.dashboard;
      return d.hasTestModel || d.hasEnvReadiness || d.hasTestPlan || d.hasTestPolicy;
    });
    if (eligible.length === 0) return state;
    const victim = prng.pick(eligible);
    const d = victim.dashboard;
    // Strip the highest-tier flag they hold.
    let dashboard = d;
    if (d.hasTestModel) dashboard = { ...d, hasTestModel: false };
    else if (d.hasEnvReadiness) dashboard = { ...d, hasEnvReadiness: false };
    else if (d.hasTestPlan) dashboard = { ...d, hasTestPlan: false };
    else if (d.hasTestPolicy) dashboard = { ...d, hasTestPolicy: false };
    return patchPlayer(state, victim.id, { dashboard });
  },
};

// -----------------------------------------------------------------------------
// Card 8: Test Tool Adoption (bonus)
// -----------------------------------------------------------------------------

const testToolAdoption: EventCardDef = {
  id: 'evt-test-tool-adoption',
  displayName: 'Test Tool Adoption',
  description:
    'Tooling investment pays off. Each player with both Test Plan and Test Model gains 1 reserveCube.',
  isoClauseRef: 'ISO/IEC/IEEE 29119-3:2021 §6.2.7 (Standards) / §3.16 (Test model)',
  kind: 'bonus',
  effect: (state) => {
    let next = state;
    for (const player of state.players) {
      if (player.dashboard.hasTestPlan && player.dashboard.hasTestModel) {
        next = patchPlayerReserve(next, player.id, 1);
      }
    }
    return next;
  },
};

// -----------------------------------------------------------------------------
// Card 9: Successful Audit (bonus)
// -----------------------------------------------------------------------------

const successfulAudit: EventCardDef = {
  id: 'evt-successful-audit',
  displayName: 'Successful Audit',
  description:
    'Audit passes. Each player holding Test Policy gains 1 reserveCube per fully-controlled module.',
  isoClauseRef: 'ISO/IEC/IEEE 29119-3:2021 §6.2 (Test policy) / §4.1.2 (Full conformance)',
  kind: 'bonus',
  effect: (state) => {
    let next = state;
    for (const player of state.players) {
      if (!player.dashboard.hasTestPolicy) continue;
      const moduleCount = modulesFullyControlledBy(state, player.id);
      if (moduleCount > 0) {
        next = patchPlayerReserve(next, player.id, moduleCount);
      }
    }
    return next;
  },
};

// -----------------------------------------------------------------------------
// Card 10: Data Breach
// -----------------------------------------------------------------------------

const dataBreach: EventCardDef = {
  id: 'evt-data-breach',
  displayName: 'Data Breach',
  description:
    'PII leak. Each player without a Test Plan (which carries the Test Data Requirements section) loses one random territory to neutral.',
  isoClauseRef: 'ISO/IEC/IEEE 29119-3:2021 §8.5 (Test data requirements) / §8.5.7 archiving',
  kind: 'bug',
  effect: (state, prng) => {
    let territories = { ...state.territories } as Record<string, TerritoryRuntimeState>;
    for (const player of state.players) {
      if (player.dashboard.hasTestPlan) continue;
      const owned = Object.values(territories).filter(
        (t) => t.controllingPlayerId === player.id,
      );
      if (owned.length === 0) continue;
      const victim = prng.pick(owned);
      territories = { ...territories, [victim.territoryId]: neutralise(victim) };
    }
    return { ...state, territories };
  },
};

// -----------------------------------------------------------------------------
// Card 11: Defect Triage Backlog
// -----------------------------------------------------------------------------

const defectTriageBacklog: EventCardDef = {
  id: 'evt-defect-triage-backlog',
  displayName: 'Defect Triage Backlog',
  description:
    'Bug-tracker overflow. Every territory with more than 5 cubes loses 1 cube — disperse your forces.',
  isoClauseRef: 'ISO/IEC/IEEE 29119-3:2021 §8.11.7 (Originator\'s assessment of priority)',
  kind: 'bug',
  effect: (state) => {
    const next: Record<string, TerritoryRuntimeState> = {};
    for (const [tid, t] of Object.entries(state.territories)) {
      if (t.controllingPlayerId && t.cubeCount > 5) {
        next[tid] = { ...t, cubeCount: t.cubeCount - 1 };
      } else {
        next[tid] = t;
      }
    }
    return { ...state, territories: next };
  },
};

// -----------------------------------------------------------------------------
// Card 12: Environment Outage
// -----------------------------------------------------------------------------

const environmentOutage: EventCardDef = {
  id: 'evt-environment-outage',
  displayName: 'Environment Outage',
  description:
    'Test environment goes down. Every territory without Env-Readiness loses 1 cube (minimum 1 remains).',
  isoClauseRef: 'ISO/IEC/IEEE 29119-3:2021 §8.6 / §8.8 (Test environment readiness report)',
  kind: 'system',
  effect: (state) => {
    const next: Record<string, TerritoryRuntimeState> = {};
    for (const [tid, t] of Object.entries(state.territories)) {
      if (t.controllingPlayerId && !t.hasEnvReadiness && t.cubeCount > 1) {
        next[tid] = { ...t, cubeCount: t.cubeCount - 1 };
      } else {
        next[tid] = t;
      }
    }
    return { ...state, territories: next };
  },
};

// -----------------------------------------------------------------------------
// Card 13: Acceptance Test Boom (bonus)
// -----------------------------------------------------------------------------

const acceptanceTestBoom: EventCardDef = {
  id: 'evt-acceptance-test-boom',
  displayName: 'Acceptance Test Boom',
  description:
    'UAT signs off. Each player with the full Tier-3 chain (policy+plan+env+model+cases+procedures) gains floor(controlled-territories/4) reserveCubes.',
  isoClauseRef: 'ISO/IEC/IEEE 29119-3:2021 §8.3, §8.4 (Test cases + Test procedures)',
  kind: 'bonus',
  effect: (state) => {
    let next = state;
    for (const player of state.players) {
      if (!hasTier3Chain(player)) continue;
      const controlled = ownedTerritories(state, player.id).length;
      const bonus = Math.floor(controlled / 4);
      if (bonus > 0) {
        next = patchPlayerReserve(next, player.id, bonus);
      }
    }
    return next;
  },
};

// -----------------------------------------------------------------------------
// Card 14: Regression Win (bonus)
// -----------------------------------------------------------------------------

const regressionWin: EventCardDef = {
  id: 'evt-regression-win',
  displayName: 'Regression Win',
  description:
    'Regression suite catches a defect before release. Every non-eliminated player gains 1 reserveCube.',
  isoClauseRef: 'ISO/IEC/IEEE 29119-3:2021 §3.22 (Test result) / §8.10 (Test execution log)',
  kind: 'bonus',
  effect: (state) => {
    let next = state;
    for (const player of state.players) {
      if (player.eliminated) continue;
      next = patchPlayerReserve(next, player.id, 1);
    }
    return next;
  },
};

// -----------------------------------------------------------------------------
// Card 15: Test Charter Shortfall (audit)
// -----------------------------------------------------------------------------

const testCharterShortfall: EventCardDef = {
  id: 'evt-test-charter-shortfall',
  displayName: 'Test Charter Shortfall',
  description:
    'Insufficient test design. Each player without a Test Model loses one random territory\'s Env-Readiness flag.',
  isoClauseRef: 'ISO/IEC/IEEE 29119-3:2021 §3.16 (Test model) / §8.2 (Test model specification)',
  kind: 'audit',
  effect: (state, prng) => {
    let territories = { ...state.territories } as Record<string, TerritoryRuntimeState>;
    for (const player of state.players) {
      if (player.dashboard.hasTestModel) continue;
      const candidates = Object.values(territories).filter(
        (t) => t.controllingPlayerId === player.id && t.hasEnvReadiness,
      );
      if (candidates.length === 0) continue;
      const victim = prng.pick(candidates);
      territories = {
        ...territories,
        [victim.territoryId]: { ...victim, hasEnvReadiness: false },
      };
    }
    return { ...state, territories };
  },
};

// -----------------------------------------------------------------------------
// Pool registry
// -----------------------------------------------------------------------------

export const EVENT_CARDS: readonly EventCardDef[] = [
  serverCrash,
  complianceAudit,
  flakyTest,
  releaseDeadline,
  productionIncident,
  auditFailure,
  knowledgeLoss,
  testToolAdoption,
  successfulAudit,
  dataBreach,
  defectTriageBacklog,
  environmentOutage,
  acceptanceTestBoom,
  regressionWin,
  testCharterShortfall,
] as const;

export const EVENT_CARDS_BY_ID: ReadonlyMap<EventCardId, EventCardDef> = new Map(
  EVENT_CARDS.map((c) => [c.id, c] as const),
);

export function eventCardById(id: EventCardId): EventCardDef | undefined {
  return EVENT_CARDS_BY_ID.get(id);
}
